import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Lock, FileText as FileIcon, CreditCard, Truck, RotateCcw, HandshakeIcon, AlertTriangle } from "lucide-react";
import { useBusinessInfo } from "@/contexts/BusinessInfoContext";

export default function Terms() {
  const { info } = useBusinessInfo();
  const biz = info?.name || "CTP Kenya";
  const email = info?.email || "print.noreply@ctpkenya.com";
  const phone = info?.phone || "";

  const sections = [
    {
      icon: ShieldCheck,
      title: "1. Security & Data Protection",
      body: [
        "All data is transmitted over TLS/HTTPS between your device and our servers. Authentication cookies are httpOnly-secure where technically supported.",
        "Payment data (M-Pesa STK Push) is processed directly by Safaricom's Daraja API — we do NOT see or store your M-Pesa PIN. We record only the receipt reference (e.g. MPE250716.XXXX) as proof of payment against your order.",
        `Passwords are hashed with bcrypt at rest. If you register with Google, we only receive your name, email, and profile picture; no Google password is ever transmitted to ${biz}.`,
        "You may request a full copy or deletion of your personal data at any time by writing to " + email + ".",
      ],
    },
    {
      icon: FileIcon,
      title: "2. File Handling & Retention",
      body: [
        `Documents you upload (PDF, DOCX, JPG, PNG) are stored on encrypted object storage and are visible only to authenticated ${biz} staff assigned to your job.`,
        "Uploaded files are automatically purged from our servers **7 days after your order is marked completed or cancelled**. Files linked to open orders remain until fulfilment concludes.",
        "You retain full copyright and ownership of every document you upload. Uploading a document to us grants a strictly limited, non-exclusive licence to print, bind, laminate, package and deliver it — nothing more.",
        "We do not scan, index, or use your documents for any purpose other than fulfilling your specific order. Files are never shared with any third party except the physical courier for delivery-label printing.",
      ],
    },
    {
      icon: Lock,
      title: "3. Confidentiality",
      body: [
        `Every staff member of ${biz} signs a confidentiality agreement before they can view or process customer uploads. Non-privileged staff see phone numbers masked (••• ••• 993) and cannot download files until an order is claimed for printing.`,
        "We treat all personal, medical, legal, academic and business documents as strictly confidential. If your document requires additional handling (e.g. sealed envelope, in-person hand-off), please add a note when placing the order or call us directly.",
        "All admin actions — file downloads, status changes, assignment — are logged in an immutable audit trail with actor and timestamp.",
      ],
    },
    {
      icon: CreditCard,
      title: "4. Pricing & Payment",
      body: [
        "Prices shown at checkout are the final prices for the specifications selected. A small convenience fee (percentage-based, with a small minimum) applies to cover Safaricom's transaction cost.",
        "Payment is via M-Pesa STK Push initiated from the order page. If STK fails, you can tap \"Already paid? Confirm payment\" to force-query the transaction status.",
        "Business (Office Pack) members can settle orders directly from their prepaid wallet — no STK PIN required when the balance covers the order total.",
        "All prices are quoted in Kenyan Shillings (KES) and are inclusive of any applicable taxes.",
      ],
    },
    {
      icon: Truck,
      title: "5. Delivery, Pickup & Turnaround",
      body: [
        `Pickup is free at our shop${info?.address ? ` (${info.address})` : ""} during business hours.`,
        "Nairobi delivery is fulfilled by our in-house rider or a partner courier. Delivery fees are quoted separately in the Dispatch step and can be paid by M-Pesa or in cash on delivery.",
        "Countrywide delivery uses a certified courier (G4S, Wells Fargo, Fargo Courier, etc.) selected based on destination. Transit times vary by county (typically 1-3 business days).",
        "Turnaround times: Normal (within 2 hours), Urgent (within 60 minutes, +50%), Priority (within 30 minutes, +100%). These are best-effort estimates during business hours; large-volume jobs may run over — we will WhatsApp you if so.",
      ],
    },
    {
      icon: RotateCcw,
      title: "6. Cancellations, Refunds & Reprints",
      body: [
        "You may cancel an order any time before it enters the \"Printing\" status via WhatsApp or by phoning us. Refunds for cancelled orders are returned to the paying M-Pesa number within 3 business days.",
        "Once an order is in Printing, cancellations are subject to already-consumed materials (paper, ink, binding, cover).",
        `If a printed job has a defect (wrong paper size, colour bleed, missing pages, binding damage), notify us within 48 hours of delivery with photos. We will reprint at no cost or refund at our discretion. Contact: ${email}${phone ? ` · ${phone}` : ""}.`,
        "We reserve the right to refuse to print material that is illegal, hateful, sexually explicit, or infringes on third-party copyright.",
      ],
    },
    {
      icon: HandshakeIcon,
      title: "7. Your Responsibilities",
      body: [
        "You confirm that you own the copyright of documents you upload, or have permission to print them.",
        "You are responsible for verifying the specifications (page count, colour mode, paper size, binding) on the live cost preview before you confirm payment. Once an order is Paid, spec changes require a new order.",
        "Please keep your account credentials confidential. We are not liable for orders placed by a third party using your credentials without your consent.",
      ],
    },
    {
      icon: AlertTriangle,
      title: "8. Liability",
      body: [
        `Our maximum liability for any single order is limited to the amount you paid for that order.`,
        "We are not liable for lost profits, indirect damages, or consequential damages arising from delayed or damaged deliveries beyond our reasonable control (courier accidents, natural disasters, power outages, Safaricom downtime, etc.).",
        "These terms are governed by the laws of the Republic of Kenya. Any dispute will be resolved by the courts of Nairobi.",
      ],
    },
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 lg:py-16" data-testid="terms-page">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-[#525252] hover:text-[#0A0A0A] mb-6"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back home
      </Link>

      <div className="mb-10">
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252]">§ Legal</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1 mb-3">
          Terms &amp; Conditions
        </h1>
        <p className="text-[#525252] max-w-2xl">
          The rules that govern how <b>{biz}</b> handles your documents, your payments, and your data. By placing an order you agree to the terms below. Last updated: <span className="mono">Feb 2026</span>.
        </p>
      </div>

      <div className="space-y-8">
        {sections.map(({ icon: Icon, title, body }) => (
          <section key={title} className="border-l-4 border-[#059669] pl-5">
            <h2 className="flex items-center gap-3 font-display font-black text-2xl mb-3">
              <Icon className="w-6 h-6 text-[#059669]" strokeWidth={2.5} />
              {title}
            </h2>
            <div className="space-y-3 text-[15px] leading-relaxed text-[#0A0A0A]">
              {body.map((p) => (
                <p key={p.slice(0, 40)} className="[&_b]:text-[#059669]">{p.split("**").map((chunk, j) => (
                  j % 2 === 1 ? <b key={`${chunk}-${j}`}>{chunk}</b> : chunk
                ))}</p>
              ))}
            </div>
          </section>
        ))}
      </div>

      <div className="mt-14 pt-8 border-t border-[#E5E5E5]">
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252] mb-2">§ Questions?</div>
        <p className="text-sm">
          Write to <a href={`mailto:${email}`} className="mono underline decoration-[#059669] underline-offset-4 hover:text-[#059669]">{email}</a>
          {phone && <> or call <a href={`tel:${phone.replace(/\s/g, "")}`} className="mono underline decoration-[#059669] underline-offset-4 hover:text-[#059669]">{phone}</a></>}.
          We aim to respond within one business day.
        </p>
      </div>
    </div>
  );
}
